import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-xl leading-tight">
                  CureClave
                </span>
                <span className="text-[10px] font-medium text-cyan-400 tracking-wider uppercase">
                  Innovations
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Transforming healthcare through innovative technology solutions. Your partner in digital health excellence.
            </p>
            <div className="flex gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-600 hover:text-white transition-all social-icon"
                data-testid="footer-social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-600 hover:text-white transition-all social-icon"
                data-testid="footer-social-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-600 hover:text-white transition-all social-icon"
                data-testid="footer-social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-600 hover:text-white transition-all social-icon"
                data-testid="footer-social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-6">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services#consultation" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Technical Consultation
                </Link>
              </li>
              <li>
                <Link to="/services#procurement" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Procurement
                </Link>
              </li>
              <li>
                <Link to="/services#training" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Training
                </Link>
              </li>
              <li>
                <Link to="/services#deployment" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Deployment
                </Link>
              </li>
              <li>
                <Link to="/services#management" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Management
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-6">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:hello@cureclave.com" 
                  className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
                  data-testid="footer-email"
                >
                  hello@cureclave.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                <a 
                  href="tel:+447459431019" 
                  className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
                  data-testid="footer-phone"
                >
                  +44 7459 431019
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  London, United Kingdom
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} CureClave Innovations. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
